import React, { useState, useEffect } from 'react';
import { Bell, Settings, LogOut, Mail, Shield, CircleCheck, Trash2, MailOpen, MailCheck } from 'lucide-react';
import { useContext } from 'react';
import { AppContent } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from '../App';
import { toast } from 'react-toastify';

const TopNavbar = () => {
    const {
        setUserData,
        setIsLoggedin,
        notifications,
        unreadCount,
        markAsRead,
        markAsUnread,
        deleteNotification,
        markAllAsRead,
    } = useContext(AppContent);
    const { userData, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const API = useContext(ApiContext);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [hoveredNotification, setHoveredNotification] = useState(null);
    const [unreadEmailsCount, setUnreadEmailsCount] = useState(0);

    // Récupérer les informations de l'entreprise
    useEffect(() => {
        const fetchCompanyData = async () => {
            if (userData?.companyId) {
                setLoadingCompany(true);
                try {
                    const response = await API.get(`/api/companies/${userData.companyId}`);
                    setCompanyName(response.data.name);
                } catch (error) {
                    console.error('Error fetching company data:', error);
                    // Fallback sur le champ company du user si disponible
                    setCompanyName(userData.company || 'Mon Entreprise');
                } finally {
                    setLoadingCompany(false);
                }
            } else {
                // Utiliser le champ company du user ou une valeur par défaut
                setCompanyName(userData?.company || 'Mon Entreprise');
            }
        };

        if (userData && !authLoading) {
            fetchCompanyData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.companyId, userData?.company, authLoading]);

    // Récupérer le nombre de conversations non lues
    useEffect(() => {
        const fetchUnreadEmailsCount = async () => {
            try {
                const response = await API.get('/api/emails/unread-conversations-count');
                if (response.data.success) {
                    setUnreadEmailsCount(response.data.count);
                }
            } catch (error) {
                console.error('Error fetching unread emails count:', error);
            }
        };

        if (userData && !authLoading) {
            fetchUnreadEmailsCount();
            // Rafraîchir le compteur toutes les 30 secondes
            const interval = setInterval(fetchUnreadEmailsCount, 30000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading]);

    const handleNotificationClick = async (notification, e) => {
        // Empêcher la propagation si on clique sur les boutons d'action
        if (e.target.closest('.notification-action-btn')) {
            return;
        }

        // Marquer comme lue
        await markAsRead(notification._id);

        // Si c'est une notification de vérification d'email
        if (notification.type === 'email_verification') {
            try {
                const { data } = await API.post('/api/send-verify-otp');
                if (data.success) {
                    navigate('/email-verify');
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                toast.error(error.message);
            }
        }

        // Si c'est une notification liée à un reminder
        if (notification.type === 'reminder_due' && notification.relatedReminder) {
            navigate('/reminders');
        }
    };

    const handleToggleRead = async (e, notificationId, isRead) => {
        e.stopPropagation();
        if (isRead) {
            await markAsUnread(notificationId);
        } else {
            await markAsRead(notificationId);
        }
    };

    const handleDelete = async (e, notificationId) => {
        e.stopPropagation();
        await deleteNotification(notificationId);
    };

  const handleLogout = async () => {
    try {
      // Call logout API if available
      await API.post('/auth/logout');
    } catch (error) {
      console.log('Logout API call failed, proceeding with local logout');
    } 
      localStorage.removeItem('auth-token');
      sessionStorage.clear();
      window.location.href = '/';
  };

    const getUserInitials = () => {
        if (!userData?.name) return 'U';
        return userData.name
            .split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .slice(0, 2)
            .join('');
    };

    return (
        <div className="fixed top-0 left-0 right-0 h-12 sm:h-10 bg-white shadow-sm px-4 sm:px-6 z-40">
            <div className="flex items-center justify-end h-full">
                {/* Menu à droite */}
                <div className="flex items-center space-x-1 sm:space-x-2">
                    {/*
                    <button
                        onClick={() => navigate('/mailing')}
                        className="relative h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 group"
                        title="Messages"
                    >
                        <Mail size={18} className='text-slate-600 group-hover:text-slate-800'/>
                        {unreadEmailsCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium">
                                {unreadEmailsCount > 9 ? '9+' : unreadEmailsCount}
                            </span>
                        )}
                    </button>
                    */}
                    {/* Notifications */}
                    <div className='relative group'>
                        <button className="relative h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 group">
                            <Bell size={18} className="text-slate-600 group-hover:text-slate-800" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>

                        {/* Dropdown notifications */}
                        <div className='absolute hidden group-hover:block top-full right-0 w-80 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50'>
                            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-slate-800 m-0">
                                    Notifications ({unreadCount})
                                </h3>
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="text-xs text-indigo-600 hover:text-indigo-800"
                                    >
                                        Tout marquer comme lu
                                    </button>
                                )}
                            </div>

                            <div className="max-h-96 overflow-y-auto">
                                {notifications.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-slate-500 text-sm">
                                        Aucune notification
                                    </div>
                                ) : (
                                    notifications.map((notif) => (
                                        <div
                                            key={notif._id}
                                            onClick={(e) => handleNotificationClick(notif, e)}
                                            onMouseEnter={() => setHoveredNotification(notif._id)}
                                            onMouseLeave={() => setHoveredNotification(null)}
                                            className={`flex items-start px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-slate-50 ${!notif.isRead ? 'bg-indigo-50' : ''}`}
                                        >
                                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                                                notif.type === 'email_verification' ? 'bg-amber-100' : 'bg-blue-100'
                                            }`}>
                                                <Bell size={16} className={
                                                    notif.type === 'email_verification' ? 'text-amber-600' : 'text-blue-600'
                                                } />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium text-slate-800 ${!notif.isRead ? 'font-semibold' : ''}`}>
                                                    {notif.title !== 'Inconnu' ? notif.title : ''}
                                                </p>
                                                <div className="text-xs text-slate-500 mt-0.5">
                                                    {notif.message.split('\n').map((line, idx) => (
                                                        <p key={idx} className={idx === 0 ? '' : 'mt-0.5'}>
                                                            {line}
                                                        </p>
                                                    ))}
                                                </div>
                                                {notif.type === 'reminder_due' && (
                                                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">
                                                        ⚠ En retard
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-shrink-0 flex items-center gap-1">
                                                {/* Bouton marquer comme lu/non lu */}
                                                {hoveredNotification === notif._id && (
                                                    <button
                                                        onClick={(e) => handleToggleRead(e, notif._id, notif.isRead)}
                                                        className="notification-action-btn p-1.5 rounded hover:bg-slate-200 transition-all"
                                                        title={notif.isRead ? "Marquer comme non lu" : "Marquer comme lu"}
                                                    >
                                                        {notif.isRead ? (
                                                            <MailOpen size={14} className="text-slate-600" />
                                                        ) : (
                                                            <MailCheck size={14} className="text-slate-600" />
                                                        )}
                                                    </button>
                                                )}

                                                {/* Bouton supprimer */}
                                                {hoveredNotification === notif._id && (
                                                    <button
                                                        onClick={(e) => handleDelete(e, notif._id)}
                                                        className="notification-action-btn p-1.5 rounded hover:bg-red-100 transition-all"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 size={14} className="text-red-600" />
                                                    </button>
                                                )}

                                                {/* Point indicateur non lu */}
                                                {!notif.isRead && hoveredNotification !== notif._id && (
                                                    <div className="ml-1">
                                                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/reminders')}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 group"
                        title="Tâches"
                    >
                        <CircleCheck size={18} className='text-slate-600 group-hover:text-slate-800'/>
                    </button>
                    
                    {/* Settings */}
                    <button 
                        onClick={() => navigate('/settings')}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 group"
                        title="Paramètres"
                    >
                        <Settings size={18} className="text-slate-600 group-hover:text-slate-800" />
                    </button>
                    
                    {/* Divider */}
                    <div className="hidden sm:block h-4 w-px bg-slate-300 mx-2"></div>

                    {/* User Menu */}
                    <div className="relative hidden sm:block">
                        <button
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="flex items-center space-x-2 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors duration-200 group"
                        >
                            {/* Avatar */}
                            {authLoading ? (
                                <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                            ) : userData?.profilePhotoUrl ? (
                                <img
                                    src={userData.profilePhotoUrl}
                                    alt="Profil"
                                    className="w-6 h-6 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-semibold text-white">
                                        {getUserInitials()}
                                    </span>
                                </div>
                            )}
                            
                            {/* Company info */}
                            <div className="flex flex-col items-start">
                                <span className="text-xs font-medium text-slate-700 leading-none">
                                    {loadingCompany ? (
                                        <span className="animate-pulse bg-gray-200 rounded h-3 w-16 inline-block"></span>
                                    ) : (
                                        companyName
                                    )}
                                </span>
                                <div className="flex items-center space-x-1">
                                    <span className="text-xs text-slate-500 leading-none">
                                        {userData?.name || 'Utilisateur'}
                                    </span>
                                    {userData?.isAccountVerified && (
                                        <Shield size={10} className="text-green-500" />
                                    )}
                                </div>
                            </div>
                            
                            {/* Chevron */}
                            <svg 
                                className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* User Dropdown */}
                        {showUserMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50">
                                {/* User info */}
                                <div className="px-4 py-3 border-b border-slate-100">
                                    <div className="flex items-center space-x-3">
                                        {authLoading ? (
                                            <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                                        ) : userData?.profilePhotoUrl ? (
                                            <img
                                                src={userData.profilePhotoUrl}
                                                alt="Profil"
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-semibold text-white">
                                                    {getUserInitials()}
                                                </span>
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-sm font-medium text-slate-800">
                                                {userData?.name || 'Utilisateur'}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                {userData?.email}
                                            </p>
                                            <div className="flex items-center mt-1">
                                                {userData?.isAccountVerified ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                                        <Shield size={10} className="mr-1" />
                                                        Vérifié
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">
                                                        Non vérifié
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Menu items */}
                                <div className="py-1">
                                    <button
                                        onClick={() => {
                                            navigate('/settings');
                                            setShowUserMenu(false);
                                        }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <Settings size={16} className="mr-3 text-slate-400" />
                                        Paramètres
                                    </button>
                                </div>

                                {/* Logout */}
                                <div className="border-t border-slate-100 py-1">
                                    <button
                                        id='logout-button'
                                        onClick={() => {
                                            handleLogout();
                                            setShowUserMenu(false);
                                        }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <LogOut size={16} className="mr-3" />
                                        Se déconnecter
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Click outside to close menu */}
            {showUserMenu && (
                <div 
                    className="fixed inset-0 z-30" 
                    onClick={() => setShowUserMenu(false)}
                ></div>
            )}
        </div>
    );
};

export default TopNavbar;